import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  OnInit,
} from '@angular/core';
import {
  ConfirmationModalService,
  ConfirmationModalType,
} from './../../../../shared/services/confirmation-modal.service';

import { ICategoryDto } from '@models/category-dto';
import { ICompetitionBlockDto } from '@models/competition-block-dto';
import { ICompetitionDto } from '@models/competition-dto';
import { ISimpleStepParameterizationDto } from '@models/competition/step-paramaterization/dtos/simple-step-parametrization-dto';
import { StepParametrizationFilter } from '@models/competition/step-paramaterization/filters/step-parametrization-filter';
import { ParameterizationStepTypeEnum } from '@models/enums/parameterization-step-type-enum';
import { IEventDto } from '@models/event-dto';
import { IPage } from '@models/pagination/page';
import { IPageable } from '@models/pagination/pageable';
import { ChampionshipService } from '@shared/services/championship.service';
import { CompetitionBlockService } from '@shared/services/competition-block.service';
import { StepParameterizationService } from '@shared/services/competition/step-parametrization.service';
import { EventService } from '@shared/services/event.service';
import { FilterKeyEnum } from '@shared/services/filter-service/filter-keys';
import { provideFilter } from '@shared/services/filter-service/filter.service';
import { LoadingService } from '@shared/services/loading.service';
import { IsMobileService } from '@shared/services/is-mobile.service';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';
import {
  formatEnumLabel,
  openNewTab,
} from 'src/app/core/utilities/utility-functions';

@Component({
  selector: 'sigecom-step-parameterization',
  templateUrl: './step-parameterization-list.component.html',
})
export class StepParameterizationComponentList
  extends BaseComponent
  implements OnInit
{
  protected pagedSimpleStepParametrization?: IPage<ISimpleStepParameterizationDto>;
  protected stepParametrizationPageable: Partial<IPageable> = {
    page: 0,
    size: 15,
  };
  public maxDisplayedPages = 15;
  public filters$ = provideFilter<StepParametrizationFilter>(
    FilterKeyEnum.STEP_PARAMETRIZATION_FILTER,
  );

  public formatEnumLabel = formatEnumLabel;
  public stepTypes: { label: string; value: ParameterizationStepTypeEnum }[] =
    [];
  public selectedStepType: {
    label: string;
    value: ParameterizationStepTypeEnum;
  } = {} as { label: string; value: ParameterizationStepTypeEnum };

  public events?: IPage<IEventDto>;
  public eventSearchText = '';
  public eventPage = 0;
  public eventPageSize = 10;
  public competitionBlocks?: IPage<ICompetitionBlockDto>;
  public competitions?: IPage<ICompetitionDto>;
  public categories?: IPage<ICategoryDto>;
  public selectedEvent: IEventDto = {} as IEventDto;
  public selectedBlock: ICompetitionBlockDto = {};
  public selectedCompetition: ICompetitionDto = {} as ICompetitionDto;
  public selectedCategory: ICategoryDto = {};
  public categoryName?:string;
  public competitionName?:string;

  public isKeyManagement = false;
  public isRoundManagement = false;

  private readonly eventService = inject(EventService);
  private readonly competitionBlockService = inject(CompetitionBlockService);
  private readonly championshipService = inject(ChampionshipService);
  private readonly stepParametrizationService = inject(
    StepParameterizationService,
  );
  private readonly loadingService = inject(LoadingService);
  private readonly confirmationModalService = inject(ConfirmationModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly isMobileService = inject(IsMobileService);

  readonly isMobile$ = this.isMobileService.isMobile$;

  @Inject(DOCUMENT) private document: Document = new Document();

  ngOnInit(): void {
    this.activatedRoute.data.subscribe((data) => {
      const mode = data['mode'];
      this.isKeyManagement = mode === 'KEY_MANAGEMENT';
      this.isRoundManagement = mode === 'ROUND_MANAGEMENT';
    });

    this.stepTypes = Object.values(ParameterizationStepTypeEnum).map(
      (value) => ({
        label: this.formatEnumLabel(value),
        value: value,
      }),
    );
    const selectedType = this.filters$.get().petTxTipo;
    if (selectedType) {
      this.stepTypes.forEach((step) => {
        if (step.value == selectedType) {
          this.selectedStepType = step;
        }
      });
    }
    const selectedEventId = this.filters$.get().eveNrId;
    if (selectedEventId) {
      this.eventService
        .getAllCompetitionBlocksByEvent$(selectedEventId, 0, 20)
        .subscribe((blocks) => {
          this.competitionBlocks = blocks;
        });
      this.eventService.getEventById(selectedEventId).subscribe((ev) => {
        this.selectedEvent = ev;
        const selectedBlockId = this.filters$.get().bloNrId;
        if (selectedBlockId) {
          this.loadCompetitions(0);
          this.eventService
            .getAllCompetitionBlocksByEvent$(selectedEventId)
            .subscribe((blocks) => {
              this.selectedBlock =
                blocks.content?.find((b) => b.bloNrId == selectedBlockId) || {};
            });
          const selectedCompetitionId = this.filters$.get().comNrId;
          if (selectedCompetitionId) {
            this.loadCategories();
            this.competitionBlockService
              .getAllCompetitionByBlocks(selectedBlockId)
              .subscribe((competitions) => {
                this.selectedCompetition =
                  competitions.content?.find(
                    (c) => c.comNrId == selectedCompetitionId,
                  ) || ({} as ICompetitionDto);
                const selectedCategoryId = this.filters$.get().catNrId;
                if (selectedCategoryId) {
                  this.championshipService
                    .getAllCategoryByCompetition(selectedCompetitionId)
                    .subscribe((categories) => {
                      this.selectedCategory =
                        categories.content?.find(
                          (cat) => cat.catNrId == selectedCategoryId,
                        ) || {};
                    });
                }
              });
          }
        }
      });
    }

    this.loadEvents();
    this.filters$.observe().subscribe({
      next: () => {
        this.resetParametrizationPageable();
        this.getStepParametrizations();
      },
    });
  }
  resetParametrizationPageable() {
    this.stepParametrizationPageable.page = 0;
    this.stepParametrizationPageable.size = 15;
  }
  getStepParametrizations(): void {
    this.loadingService.activate();
    this.stepParametrizationService
      .getSimpleStepParametrization$(
        this.filters$.get(),
        this.stepParametrizationPageable,
      )
      .subscribe({
        next: (res) => {
          this.pagedSimpleStepParametrization = res;
          this.loadingService.deactivate();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
          this.loadingService.deactivate();
        },
      });
  }

  onEventSearch(text: string) {
    this.eventSearchText = text;
    this.loadEvents(0);
  }

  onEventPageChange(page: number) {
    this.loadEvents(page);
  }


  onCompetitionSearch(text: string) {
    this.competitionName = text;
    this.loadCompetitions(0);
  }

  onCompetitionPageChange(page: number) {
    this.loadCompetitions(page);
  }

  onCategorySearch(text: string) {
    this.categoryName = text;
    this.loadCategories(0);
  }

  onCategoryPageChange(page: number) {
    this.loadCategories(page);
  }

  loadEvents(page: number = 0) {
    this.eventPage = page;
    const trimmed = this.eventSearchText?.trim();
    const filter = trimmed ? { eveTxNome: trimmed } : undefined;

    this.eventService
      .getAllAvailableEvents(page, this.eventPageSize, filter)
      .pipe(this.takeUntilDestroy())
      .subscribe({
        next: (res) => {
          this.events = res;
          this.cdr.detectChanges();
        },
        error: (err) => this.messageErrorResolver.resolveMessage(err),
      });
  }

  loadCompetitionBlocks(eventId: number) {
    this.eventService.getAllCompetitionBlocksByEvent$(eventId).subscribe({
      next: (res) => {
        this.competitionBlocks = res;
        this.cdr.detectChanges();
      },
      error: (err) => this.messageErrorResolver.resolveMessage(err),
    });
  }

  loadCompetitions(page = 0) {
    const bloNrId = this.selectedBlock.bloNrId || this.filters$.get().bloNrId!
    this.competitionBlockService.getAllCompetitionByBlocks(
      bloNrId,
      page,
      20,
      undefined,
      undefined,
      this.competitionName
    ).subscribe({
      next: (res) => {
        this.competitions = res;
        this.cdr.detectChanges();
      },
      error: (err) => this.messageErrorResolver.resolveMessage(err),
    });
  }

  loadCategories(page = 0) {
    const comNrId = this.selectedCompetition.comNrId || this.filters$.get().comNrId!
    this.championshipService
      .getAllCategoryByCompetition(comNrId, page, 20, this.categoryName)
      .subscribe({
        next: (res) => {
          this.categories = res;
          this.cdr.detectChanges();
        },
        error: (err) => this.messageErrorResolver.resolveMessage(err),
      });
  }

  onEventChange(event: IEventDto | null) {
    if (event === null) {
      this.filters$.patch({
        eveNrId: undefined,
        bloNrId: undefined,
        comNrId: undefined,
        catNrId: undefined,
      });
      this.selectedEvent = event ?? ({} as IEventDto);
      this.selectedBlock = {};
      this.selectedCompetition = {} as ICompetitionDto;
      this.selectedCategory = {};
    }
    if (event?.eveNrId) {
      this.loadCompetitionBlocks(event.eveNrId);
      if (this.selectedEvent !== event) {
        this.filters$.patch({
          eveNrId: event.eveNrId,
          bloNrId: undefined,
          comNrId: undefined,
          catNrId: undefined,
        });
        this.selectedBlock = {};
        this.selectedCompetition = {} as ICompetitionDto;
        this.selectedCategory = {};
        this.selectedEvent = event ?? ({} as IEventDto);
      }
    }
  }

  onBlockChange(block: ICompetitionBlockDto | null) {
    block === null &&
      this.filters$.patch({
        bloNrId: undefined,
        comNrId: undefined,
        catNrId: undefined,
      });
    this.selectedBlock = block ?? {};
    this.selectedCompetition = {} as ICompetitionDto;
    this.selectedCategory = {};
    if (block?.bloNrId) {
      this.loadCompetitions();
      this.filters$.patch({ bloNrId: block?.bloNrId });
    }
  }

  onCompetitionChange(competition: ICompetitionDto | null) {
    competition === null &&
      this.filters$.patch({
        comNrId: undefined,
        catNrId: undefined,
      });
    this.selectedCompetition = competition ?? ({} as ICompetitionDto);
    this.selectedCategory = {};
    if (competition?.comNrId) {
      this.loadCategories();
      this.filters$.patch({ comNrId: competition?.comNrId });
    }
  }

  protected navigateToCreate() {
    this.router.navigate(['parametrizacao-etapa', 'criar']).then();
  }

  public onPageChange(page: number): void {
    this.stepParametrizationPageable.page = page;
    this.getStepParametrizations();
  }

  deleteStepParametrization(item: ISimpleStepParameterizationDto) {
    this.confirmationModalService.openModal({
      modalType: ConfirmationModalType.danger,
      confirmText: 'Deletar',
      cancelText: 'Cancelar',
      message: `Tem certeza que deseja deletar`,
      callbackExecuteAction: () => {
        this.loadingService.activate();
        this.stepParametrizationService
          .deleteStepParametrization(item.petNrId!)
          .subscribe({
            next: () => {
              this.snackbarService.showSuccess(
                `${item.petTxNome} deletado com sucesso!`,
              );
              this.getStepParametrizations();
            },
          });
      },
    });
  }
  filterByName(petTxNome: string) {
    this.filters$.patch({ petTxNome: petTxNome });
  }
  filterByStepType(
    event: { label: string; value: ParameterizationStepTypeEnum } | null,
  ) {
    this.filters$.patch({ petTxTipo: event?.value ?? undefined });
  }
  filterByCategory(category: ICategoryDto | null) {
    this.selectedCategory = category || {};
    this.filters$.patch({ catNrId: category?.catNrId ?? undefined });
  }
  navigateToEdit(event: ISimpleStepParameterizationDto) {
      this.router
        .navigate(['parametrizacao-etapa', 'editar', event.petNrId])
        .then();
  }
  navigateToEditKey(event: ISimpleStepParameterizationDto) {
    this.router
      .navigate(['parametrizacao-etapa', 'gerenciar-chaves', event.petNrId])
      .then();
  }
  navigateToEditRoundWithLocal(event: ISimpleStepParameterizationDto) {
    this.router
      .navigate(['parametrizacao-etapa', 'gerenciar-rodadas-com-local', event.petNrId])
      .then();
  }
  navigateToEditRound(event: ISimpleStepParameterizationDto) {
    this.router
      .navigate(['parametrizacao-etapa', 'gerenciar-rodadas', event.petNrId])
      .then();
  }
  public openEditInNewTab(fase: ISimpleStepParameterizationDto): void {
    openNewTab(
      ['/', 'parametrizacao-etapa', 'editar', fase.petNrId],
      this.router,
      this.document,
    );
  }
  get hasFilters(): boolean {
    const f = this.filters$.get()!;
    return (
      f.petNrId !== undefined ||
      f.eveNrId !== undefined ||
      f.bloNrId !== undefined ||
      f.comNrId !== undefined ||
      !!f.petTxNome ||
      !!f.petTxDescricao ||
      f.petNrMaxJogosDia !== undefined ||
      f.petTmDuracaoEmb !== undefined ||
      f.petNrMaxEmbSimultaneos !== undefined ||
      f.petBlTrocaOrdemConfronto !== undefined ||
      f.petNrQtdInc !== undefined ||
      f.petNrQtdChe !== undefined ||
      f.cheNrIdPrincipal !== undefined ||
      f.catNrId !== undefined ||
      !!f.catTxNome ||
      f.petTxTipo !== undefined
    );
  }
  clearFilters(): void {
    this.filters$.clear();
    this.selectedStepType = {} as {
      label: string;
      value: ParameterizationStepTypeEnum;
    };
    this.selectedEvent = {} as IEventDto;
    this.selectedBlock = {};
    this.selectedCompetition = {} as ICompetitionDto;
    this.events = undefined;
    this.competitionBlocks = undefined;
    this.competitions = undefined;
    this.categories = undefined;
    this.eventSearchText = '';
    this.eventPage = 0;

    this.loadEvents();
    this.getStepParametrizations();
    this.cdr.detectChanges();
  }
}
